import { ArrowLeft, ChevronDown, Mail, MailOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { statusTone } from "./Card";
import { useLabels } from "./useLabels";
import { useTicketContext } from "./useTicket";

/** Ticket number, status pill (choose to change), read/unread switch, back, More Actions. */
export function Header() {
  const { detail, lookups, save, saving } = useTicketContext();
  const navigate = useNavigate();
  const t = detail.ticket;
  const unreadCount = detail.messages.filter((m) => m.unread).length + detail.texts.filter((m) => m.unread).length;
  const unread = unreadCount > 0 || !!t.markedUnreadAt;
  const statuses = lookups?.statuses.filter((s) => s.isActive || s.id === t.statusId) ?? [{ id: t.statusId, name: t.status }];
  const [menu, setMenu] = useState(false);
  const labels = useLabels();
  const outbound = detail.labels.find((l) => !l.toShop && !l.voidedAt && l.labelUrl);
  const canReplace = !!outbound && !["TRANSIT", "DELIVERED", "RETURNED", "FAILURE"].includes((outbound.trackingStatus ?? "").toUpperCase());
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="th">
      <h2>Ticket #{t.ticketNo}</h2>
      <label className={`pill-select ${statusTone(t.status, t.statusIsClosed)}`} title="Change status">
        <span className="sr-only">Status</span>
        <select value={t.statusId} disabled={saving}
          onChange={(e) => void save("PATCH", "", { statusId: Number(e.target.value), expectedUpdatedAt: t.updatedAt })}>
          {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <ChevronDown size={14} aria-hidden="true" />
      </label>
      {unread ? (
        <button type="button" className="pill outline-blue read-toggle" disabled={saving} onClick={() => void save("POST", "/mark-read")}
          title={t.markedUnreadAt && !unreadCount ? "Marked unread by staff. Click to mark read." : "Click to mark everything on this ticket read"}>
          <span className="dot" />Unread{unreadCount ? ` (${unreadCount})` : ""}<span className="sep">·</span><MailOpen size={15} />Mark read
        </button>
      ) : (
        <button type="button" className="pill outline-grey read-toggle" disabled={saving} onClick={() => void save("POST", "/mark-unread")}
          title="Flag this ticket for attention in the work queue">
          Read<span className="sep">·</span><Mail size={15} />Mark unread
        </button>
      )}
      <span className="th-spacer" />
      <Link to="/tickets" className="btn outline"><ArrowLeft size={16} aria-hidden="true" />Back to Tickets</Link>
      <div className="menu" ref={menuRef}>
        <button type="button" className="btn outline" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
          More Actions <ChevronDown size={16} aria-hidden="true" />
        </button>
        {menu && (
          <div className="menu-list" role="menu">
            <button role="menuitem" type="button" onClick={() => { setMenu(false); navigate("/print/invoices", { state: { ticketNos: [t.ticketNo] } }); }}>Print invoice</button>
            <button role="menuitem" type="button" disabled title="Comes with the new check-in (photos and Zebra slips)">Print check-in tag</button>
            <button role="menuitem" type="button" disabled={!!labels.busy || !canReplace}
              title={canReplace ? "Void labels 2 and 3 (refund requested) and buy new ones" : "Only before the carrier scans the package, once labels have been bought"}
              onClick={() => { setMenu(false); labels.replace(); }}>Replace Shipping Labels</button>
            <button role="menuitem" type="button" disabled title="Comes with the Shippo connection">Replace return label (#1)</button>
          </div>
        )}
      </div>
    </div>
  );
}
